/**
 * Simple Jackpots Store - Based on Old Lobby Approach
 *
 * Key Concept: Single source of animated values that everyone reads from
 * - Store maintains current animated values for all jackpots/levels
 * - Animation loop updates these values (interpolation between API calls)
 * - Components just read the current animated values
 * - No individual component animation logic needed
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type IJackpotStat from '../../../types/models/IJackpotStat';
import { fetchJackpotStats } from 'core/api/endpoints/jackpot.api';
import { getCookie } from 'utils/helpers';
import { formatCurrencyValue, JACKPOT_NAMES } from '@repo/common';
import { isStaticJackpotLevel } from 'core/config/jackpotAnimationConfig';
import { idleManager } from 'core/services/managers/IdleManager';

// Simple animated value structure
interface AnimatedJackpotValue {
  jackpotName: string;
  levelId: number; // 1=GRAND, 2=MAJOR, 3=MINOR, 4=MINI
  currentValue: number; // Current animated value
  targetValue: number;  // Target from API
  formattedValue: string; // Formatted display value
}

interface JackpotsState {
  // Raw API data
  jackpotsFromAPI: Map<string, IJackpotStat>;
  previousJackpotsFromAPI: Map<string, IJackpotStat>; // Previous fetch for comparison

  // Animated values that components read from (like old JACKPOT_STATS_COUNTER)
  animatedValues: Map<string, AnimatedJackpotValue>; // key: "jackpotName:levelId"

  // Animation state
  isAnimating: boolean;
  pollingInterval: number | null;

  // Fetch tracking (like old lobby)
  isFirstFetch: boolean;
  fetchCount: number;

  // Metadata
  currency: string;
  hasData: boolean;

  // Actions
  setJackpotsFromAPI: (jackpots: Map<string, IJackpotStat>) => void;
  getAnimatedValue: (jackpotName: string, levelId?: number) => number;
  getFormattedValue: (jackpotName: string, levelId?: number) => string;
  getJackpotBonusValue: (jackpotName: string, level?: number) => string;
  getJackpotByName: (name: string) => IJackpotStat | undefined;
  startPolling: (apiHost: string, currency: string, intervalMs?: number) => void;
  stopPolling: () => void;
  fetchJackpots: (apiHost: string, currency: string) => Promise<void>;
  startAnimation: () => void;
}

const USE_MOCK = getCookie('useMockJackpot') === 'true';
const ANIMATION_FPS = 30; // 30 FPS is visually smooth for number ticking and halves CPU cost
const FETCH_INTERVAL_SECONDS = 10;

// Animation variables (outside store to avoid re-renders)
let animationIteration = 0;
let previousValues = new Map<string, AnimatedJackpotValue>();
// Keep animationFrameId outside Zustand state to avoid triggering subscribers on every RAF callback
let _animationFrameId: number | null = null;

export const useJackpotsStore = create<JackpotsState>()(
  devtools<JackpotsState>(
    (set, get) => ({
      jackpotsFromAPI: new Map(),
      previousJackpotsFromAPI: new Map(),
      animatedValues: new Map(),
      isAnimating: false,
      pollingInterval: null,
      isFirstFetch: true,
      fetchCount: 0,
      currency: 'USD',
      hasData: false,

      setJackpotsFromAPI: (jackpots: Map<string, IJackpotStat>) => {
        const currentState = get();
        const newFetchCount = currentState.fetchCount + 1;

        // Setting jackpots from API

        // Store previous API data for comparison
        const previousJackpotsFromAPI = new Map(currentState.jackpotsFromAPI);

        // Convert API data to animated values
        const newAnimatedValues = new Map<string, AnimatedJackpotValue>();

        jackpots.forEach((jackpot) => {
          if (jackpot.levelStats) {
            jackpot.levelStats.forEach((levelStat) => {
              const key = `${jackpot.jackpotName}:${levelStat.levelId}`;
              const currencyValue = levelStat.currentValue?.find((val) => val.key === jackpot.currency);
              const targetValue = currencyValue?.value || 0;

              const animatedValue: AnimatedJackpotValue = {
                jackpotName: jackpot.jackpotName,
                levelId: levelStat.levelId,
                currentValue: targetValue, // Start with target value (will be overridden for animation)
                targetValue: targetValue,
                formattedValue: formatCurrencyValue(Math.round(targetValue), 100),
              };

              newAnimatedValues.set(key, animatedValue);
            });
          }
        });

        // First fetch: just show the values statically
        if (currentState.isFirstFetch) {
          // Register with IdleManager when we have data for the first time
          idleManager.registerProcess(
            'jackpot-card-animation',
            () => {
              const state = get();
              if (state.hasData && state.animatedValues.size > 0) {
                state.startAnimation();
              }
            },
            () => {
              if (_animationFrameId) {
                cancelAnimationFrame(_animationFrameId);
                _animationFrameId = null;
                set({ isAnimating: false });
              }
            }
          );

          set({
            jackpotsFromAPI: jackpots,
            previousJackpotsFromAPI,
            animatedValues: newAnimatedValues,
            hasData: true,
            isFirstFetch: false,
            fetchCount: newFetchCount,
          });

          // First fetch complete
        } else {
          // Second+ fetch: compare with previous and start animation if different

          let hasChanges = false;

          // Check if any values changed
          newAnimatedValues.forEach((newValue, key) => {
            const previousAnimated = currentState.animatedValues.get(key);
            if (previousAnimated && Math.abs(newValue.targetValue - previousAnimated.targetValue) > 0.01) {
              hasChanges = true;
              // Keep current animated value, but update target
              newValue.currentValue = previousAnimated.currentValue;
              newValue.formattedValue = formatCurrencyValue(Math.round(previousAnimated.currentValue), 100);
            }
          });

          set({
            jackpotsFromAPI: jackpots,
            previousJackpotsFromAPI,
            animatedValues: newAnimatedValues,
            hasData: true,
            fetchCount: newFetchCount,
          });

          // Restart animation if values changed (whether running or not)
          if (hasChanges) {
            get().startAnimation(); // This will stop existing animation and start fresh
          }
        }
      },

      getAnimatedValue: (jackpotName: string, levelId?: number): number => {
        const state = get();

        if (levelId !== undefined) {
          // Get specific level value
          const key = `${jackpotName}:${levelId}`;
          const value = state.animatedValues.get(key)?.currentValue || 0;

          return value;
        } else {
          // Get maximum value across all levels (for game cards)
          const allValues = Array.from(state.animatedValues.values())
            .filter(val => val.jackpotName === jackpotName)
            .map(val => val.currentValue);
          const maxValue = Math.max(...allValues, 0);

          return maxValue;
        }
      },

      getFormattedValue: (jackpotName: string, levelId?: number): string => {
        if (levelId !== undefined) {
          // Get specific level formatted value
          const key = `${jackpotName}:${levelId}`;
          return get().animatedValues.get(key)?.formattedValue || '0.00';
        } else {
          // Get maximum value formatted (for game cards)
          const maxValue = get().getAnimatedValue(jackpotName);
          return formatCurrencyValue(Math.round(maxValue), 100);
        }
      },

      getJackpotBonusValue: (jackpotName: string, level?: number): string => {
        const jackpot = get().jackpotsFromAPI.get(jackpotName);
        
        if (!jackpot || !jackpot.levelStats) return '';

        // If level is specified (for jackpot panels), get specific level value
        if (level !== undefined) {
          const levelStat = jackpot.levelStats.find((val) => val.levelId === level);

          if (!levelStat || !levelStat.bonusValue) return '';

          // Find value for the jackpot's currency
          const currencyValue = levelStat.bonusValue.find((val) => val.key === jackpot.currency);
          return formatCurrencyValue(currencyValue?.value || 0, 100);
        }

        const maxBonusValue = jackpot.levelStats.reduce((max, levelStat) => {
          const currencyValue = levelStat.bonusValue?.find((val) => val.key === jackpot.currency);
          return Math.max(max, currencyValue?.value || 0);
        }, 0);
        return formatCurrencyValue(maxBonusValue, 100);
      },

      getJackpotByName: (name: string) => {
        return get().jackpotsFromAPI.get(name);
      },

      startAnimation: () => {
        if (_animationFrameId) {
          cancelAnimationFrame(_animationFrameId);
          _animationFrameId = null;
        }

        // Store current values as previous for interpolation
        previousValues = new Map(get().animatedValues);
        animationIteration = 0;
        let lastTimestamp = 0;
        const frameInterval = 1000 / ANIMATION_FPS;

        const animate = (timestamp: number) => {
          // Throttle to target FPS — just schedule next frame, no state updates
          if (timestamp - lastTimestamp < frameInterval) {
            _animationFrameId = requestAnimationFrame(animate);
            return;
          }
          lastTimestamp = timestamp;
          animationIteration++;

          const currentState = get();
          const animatedValues = currentState.animatedValues;

          // Check if all values are close to targets — stop animating to save CPU
          let allValuesClose = true;
          let hasAnyChange = false;

          animatedValues.forEach((current) => {
            if (!isStaticJackpotLevel(current.jackpotName, current.levelId)) {
              if (Math.abs(current.targetValue - current.currentValue) > 5) {
                allValuesClose = false;
              }
            }
          });

          if ((allValuesClose && !USE_MOCK) || (USE_MOCK && animationIteration > ANIMATION_FPS * FETCH_INTERVAL_SECONDS)) {
            _animationFrameId = null;
            set({ isAnimating: false });
            return;
          }

          // Mutate values in-place, then notify once via shallow clone
          animatedValues.forEach((current, key) => {
            const previous = previousValues.get(key);
            if (!previous) return;

            const isStatic = isStaticJackpotLevel(current.jackpotName, current.levelId);
            let newCurrentValue = current.currentValue;

            if (USE_MOCK) {
              newCurrentValue = previous.currentValue + animationIteration;
            } else if (isStatic) {
              newCurrentValue = current.targetValue;
            } else {
              const difference = current.targetValue - current.currentValue;
              const increment = difference * 0.015; // Slightly faster convergence at 30 FPS

              if (Math.abs(increment) < 0.01) {
                newCurrentValue = current.targetValue;
              } else {
                newCurrentValue = current.currentValue + increment;
                if ((difference > 0 && newCurrentValue > current.targetValue) ||
                    (difference < 0 && newCurrentValue < current.targetValue)) {
                  newCurrentValue = current.targetValue;
                }
              }
            }

            if (newCurrentValue !== current.currentValue) {
              // Only reformat when the rounded (displayed) value actually changes
              const newRounded = Math.round(newCurrentValue);
              const oldRounded = Math.round(current.currentValue);
              current.currentValue = newCurrentValue;
              if (newRounded !== oldRounded) {
                current.formattedValue = formatCurrencyValue(newRounded, 100);
              }
              hasAnyChange = true;
            }
          });

          // Only trigger subscribers when something actually changed
          if (hasAnyChange) {
            set({ animatedValues: new Map(animatedValues) });
          }

          _animationFrameId = requestAnimationFrame(animate);
        };

        _animationFrameId = requestAnimationFrame(animate);
        set({ isAnimating: true });
      },

      fetchJackpots: async (apiHost: string, currency: string) => {
        try {
          const jackpots = await fetchJackpotStats(apiHost, currency, 0);
          get().setJackpotsFromAPI(jackpots);
        } catch (error) {
          console.error('[JackpotsStore] Failed to fetch jackpots:', error);
        }
      },

      startPolling: (apiHost: string, currency: string, intervalMs = 10000) => {
        const { stopPolling, fetchJackpots } = get();

        // Stop any existing polling
        stopPolling();

        // Immediate first fetch
        fetchJackpots(apiHost, currency);

        // Set up polling interval
        const interval = setInterval(() => {
          fetchJackpots(apiHost, currency);
        }, intervalMs);

        set({ pollingInterval: interval as unknown as number });
      },

      stopPolling: () => {
        const { pollingInterval } = get();

        if (pollingInterval) {
          clearInterval(pollingInterval);
        }

        if (_animationFrameId) {
          cancelAnimationFrame(_animationFrameId);
          _animationFrameId = null;
        }

        set({
          pollingInterval: null,
          isAnimating: false,
        });
      },
    }),
    { name: 'JackpotsStoreSimple' },
  ),
);

// Selector hooks for easy component access
// fallow-ignore-next-line unused-export
export const useAnimatedJackpotValue = (jackpotName: string, levelId?: number) =>
  useJackpotsStore((state) => state.getAnimatedValue(jackpotName, levelId));

export const useFormattedJackpotValue = (jackpotName: string, levelId?: number) =>
  useJackpotsStore((state) => state.getFormattedValue(jackpotName, levelId));

/**
 * Resolves the jackpot instance a game belongs to by scanning the `games` list
 * of every jackpot instance. Premium Link games are listed both under their
 * individual jackpot (e.g. "Burning Hot") and under the "Premium Link" group,
 * so the group is skipped to return the individual jackpot name.
 */
export const useJackpotNameByGameKey = (gameKey: string | null | undefined) =>
  useJackpotsStore((state) => {
    if (!gameKey) return undefined;
    for (const [name, jackpot] of Array.from(state.jackpotsFromAPI.entries())) {
      if (name === JACKPOT_NAMES.PREMIUM_LINK) continue;
      if (jackpot.games?.includes(gameKey)) return name;
    }
    return undefined;
  });
